import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PERMISSIONS, ROLE_PERMISSIONS, PERMISSION_KEYS } from '../../config/permissions';
import { useAuth } from '../../context/AuthContext';

const ROLE_LABELS = {
  admin_master: 'Administrador maestro',
  admin_site: 'Administrador de sitio',
  admin_analytics: 'Administrador de análisis',
  admin: 'Administrador',
  analyst: 'Analista',
  operator: 'Operador de calidad',
  operator_conversion: 'Operador de conversión',
  supervisor_conversion: 'Supervisor de conversión',
};
const ROLE_SHORT_LABELS = { admin_master: 'Maestro', admin_site: 'Admin. sitio', admin_analytics: 'Admin. análisis', admin: 'Admin.', analyst: 'Analista', operator: 'Oper. calidad', operator_conversion: 'Oper. conversión', supervisor_conversion: 'Superv. conversión' };
const ROLES = Object.keys(ROLE_PERMISSIONS);
const samePermissions = (left = [], right = []) => left.length === right.length
  && left.every((permission) => right.includes(permission));

const Permissions = () => {
  const { refreshPermissions } = useAuth();
  const roles = ROLES;
  const [rolePermissions, setRolePermissions] = useState(ROLE_PERMISSIONS);
  const [savedRolePermissions, setSavedRolePermissions] = useState(ROLE_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const loadMatrix = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/permissions/matrix');
      const matrix = data?.roles || ROLE_PERMISSIONS;
      setRolePermissions(matrix);
      setSavedRolePermissions(matrix);
    } catch (error) {
      toast.error(error.response?.data?.message || 'No fue posible cargar la matriz de permisos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const changedRoles = useMemo(() => roles.filter((role) => !samePermissions(rolePermissions[role], savedRolePermissions[role])), [rolePermissions, savedRolePermissions, roles]);

  const toggleFullAccess = (role) => {
    setRolePermissions((prev) => {
      const next = { ...prev };
      if ((prev[role] || []).includes('*')) {
        next[role] = [];
      } else {
        next[role] = ['*'];
      }
      return next;
    });
  };

  const togglePermission = (role, permKey) => {
    setRolePermissions((prev) => {
      const current = prev[role] || [];
      const hasAll = current.includes('*');
      const normalized = hasAll
        ? PERMISSION_KEYS.filter((permission) => permission !== permKey)
        : current;
      if (hasAll) {
        return { ...prev, [role]: normalized };
      }
      const hasPerm = normalized.includes(permKey);
      const nextPerms = hasPerm
        ? normalized.filter((p) => p !== permKey)
        : [...normalized, permKey];
      return { ...prev, [role]: nextPerms };
    });
  };

  const saveChanges = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put('/permissions/matrix', { roles: rolePermissions });
      const matrix = data?.roles || rolePermissions;
      setRolePermissions(matrix);
      setSavedRolePermissions(matrix);
      setSavedAt(new Date().toLocaleString());
      await refreshPermissions();
      toast.success('Matriz de permisos actualizada');
    } catch (error) {
      const detail = error.response?.data?.message || error.response?.data?.errors?.roles?.[0] || 'No fue posible guardar los permisos';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setRolePermissions(savedRolePermissions);
  };

  const busy = loading || saving;
  const hasChanges = changedRoles.length > 0;

  return (
    <div className="w-full px-0 py-2">
      <div className="sticky top-0 z-10 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white border border-slate-200 px-4 py-2 shadow-sm">
        <div>
          <h1 className="m-0 text-2xl font-extrabold text-blue-900">Matriz de permisos</h1>
          <p className="m-0 text-sm text-slate-600">Compara los roles y marca los accesos permitidos.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-600" role="status">
            {loading ? 'Cargando permisos…' : hasChanges
              ? `Cambios pendientes en ${changedRoles.length} rol${changedRoles.length === 1 ? '' : 'es'}`
              : savedAt ? `Guardado: ${savedAt}` : 'Sin cambios pendientes'}
          </span>
          <button type="button" onClick={discardChanges} disabled={busy || !hasChanges}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
            Descartar cambios
          </button>
          <button type="button" onClick={saveChanges} disabled={busy || !hasChanges}
            className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-40">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" aria-label="Matriz de permisos" aria-busy={busy}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] table-fixed text-xs 2xl:text-sm">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th scope="col" className="w-[28%] px-3 py-2 text-left font-semibold">Permiso</th>
                {roles.map((role) => <th key={role} scope="col" title={ROLE_LABELS[role]}
                  className="px-1 py-2 text-center font-semibold leading-tight">
                  {ROLE_SHORT_LABELS[role]}
                  {changedRoles.includes(role) && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" title="Cambios pendientes" />}
                </th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-blue-200 bg-blue-50">
                <th scope="row" className="px-3 py-1.5 text-left font-bold text-blue-900">Acceso total</th>
                {roles.map((role) => <td key={role} className="px-1 py-1.5 text-center">
                  <input type="checkbox" aria-label={`Acceso total: ${ROLE_LABELS[role]}`} checked={(rolePermissions[role] || []).includes('*')}
                    onChange={() => toggleFullAccess(role)} disabled={busy} className="h-4 w-4 cursor-pointer align-middle accent-blue-700 disabled:opacity-50" />
                </td>)}
              </tr>
              {PERMISSIONS.map(({ key, label }, index) => <tr key={key} className={`border-b border-slate-100 last:border-0 ${index % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                <th scope="row" title={key} className="px-3 py-1 text-left font-medium leading-tight text-slate-700">{label}</th>
                {roles.map((role) => {
                  const enabled = (rolePermissions[role] || []).includes('*') || (rolePermissions[role] || []).includes(key);
                  return <td key={role} className={`px-1 py-1 text-center ${enabled ? 'bg-green-50/60' : ''}`}>
                    <input type="checkbox" aria-label={`${label}: ${ROLE_LABELS[role]}`} checked={enabled}
                      onChange={() => togglePermission(role, key)} disabled={busy} className="h-4 w-4 cursor-pointer align-middle accent-green-600 disabled:opacity-50" />
                  </td>;
                })}
              </tr>)}
            </tbody>
          </table>
        </div>
      </section>
      <p className="mb-0 mt-2 text-xs text-slate-500">Los cambios se aplican a todos los usuarios de cada rol al guardar. Casilla marcada = acceso permitido.</p>
    </div>
  );
};

export default Permissions;
